import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

import type { DocVersionMetadata } from "@xop/dashboard-shared";

export type VideoKind = "upload" | "youtube" | "vimeo" | "embed";

export interface VideoFields {
  kind: VideoKind;
  external_url: string;
  thumbnail_url: string;
  captions_url: string;
  duration_seconds: number | "";
  chapters: Array<{ time: number; title: string }>;
}

export const EMPTY_VIDEO_FIELDS: VideoFields = {
  kind: "upload",
  external_url: "",
  thumbnail_url: "",
  captions_url: "",
  duration_seconds: "",
  chapters: [],
};

/** Parse `hh:mm:ss` / `mm:ss` / `seconds` into a number of seconds.
 * Returns 0 on garbage so the row stays editable (the user can keep typing). */
export function parseTimecode(s: string): number {
  const trimmed = s.trim();
  if (!trimmed) return 0;
  if (/^\d+(\.\d+)?$/.test(trimmed)) return Math.max(0, Math.floor(Number(trimmed)));
  const parts = trimmed.split(":").map((p) => p.trim());
  if (parts.length < 2 || parts.length > 3) return 0;
  const nums = parts.map((p) => Number(p));
  if (nums.some((n) => !Number.isFinite(n) || n < 0)) return 0;
  let s_ = 0;
  if (parts.length === 3) {
    const [h, m, sec] = nums as [number, number, number];
    s_ = h * 3600 + m * 60 + sec;
  } else {
    const [m, sec] = nums as [number, number];
    s_ = m * 60 + sec;
  }
  return Math.max(0, Math.floor(s_));
}

/** Format `seconds` as `mm:ss` or `h:mm:ss` for the input round-trip. */
export function formatTimecode(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Re-shape a video-fields editor into the metadata blob the Rails
 * admin controller persists on `DocumentVersion.metadata`. */
export function toMetadata(v: VideoFields): DocVersionMetadata {
  return {
    video: {
      kind: v.kind,
      duration: v.duration_seconds === "" ? null : Math.max(0, Math.floor(Number(v.duration_seconds))),
      thumbnail: v.thumbnail_url.trim() || null,
      captions_url: v.captions_url.trim() || null,
      chapters: v.chapters
        .map((c) => ({ time: Math.max(0, Math.floor(c.time)), title: c.title.trim() }))
        .filter((c) => c.title.length > 0)
        .sort((a, b) => a.time - b.time),
    },
  };
}

/** Inverse of `toMetadata` — used on edit to seed the form from an
 * existing document version. Tolerant of partial / missing blocks. */
export function fromMetadata(meta: Record<string, unknown> | null | undefined, externalUrl: string | null | undefined, thumbnailUrl: string | null | undefined): VideoFields {
  const video = (meta as { video?: Record<string, unknown> } | null)?.video ?? {};
  const rawChapters = (video as { chapters?: unknown }).chapters;
  const chapters = Array.isArray(rawChapters)
    ? rawChapters
        .map((c) => (c && typeof c === "object" ? (c as { time?: unknown; title?: unknown }) : null))
        .filter((c): c is { time?: unknown; title?: unknown } => c !== null)
        .map((c) => ({
          time: Math.max(0, Math.floor(Number((c.time as number | string | undefined) ?? 0))) || 0,
          title: String(c.title ?? "").trim(),
        }))
        .filter((c) => c.title.length > 0)
    : [];
  const kindRaw = (video as { kind?: unknown }).kind;
  const kind: VideoKind =
    kindRaw === "youtube" || kindRaw === "vimeo" || kindRaw === "embed" || kindRaw === "upload"
      ? kindRaw
      : externalUrl
        ? deriveKindFromUrl(externalUrl)
        : "upload";
  const duration = (video as { duration?: unknown }).duration;
  return {
    kind,
    external_url: externalUrl?.trim() ?? "",
    thumbnail_url: thumbnailUrl?.trim() ?? String((video as { thumbnail?: unknown }).thumbnail ?? ""),
    captions_url: String((video as { captions_url?: unknown }).captions_url ?? ""),
    duration_seconds: typeof duration === "number" && duration > 0 ? Math.floor(duration) : "",
    chapters,
  };
}

/** Which player a pasted video link needs — shared with the release Trainings
 * dialog, which files a linked training video with the same kind. */
export function deriveKindFromUrl(url: string): VideoKind {
  if (/youtu\.?be/.test(url)) return "youtube";
  if (/vimeo\.com/.test(url)) return "vimeo";
  return "embed";
}

export function VideoFieldsEditor({
  value, onChange,
}: { value: VideoFields; onChange: (next: VideoFields) => void }) {
  const set = <K extends keyof VideoFields>(k: K, v: VideoFields[K]) =>
    onChange({ ...value, [k]: v });

  const addChapter = () =>
    set("chapters", [...value.chapters, { time: 0, title: "" }]);

  const removeChapter = (idx: number) =>
    set("chapters", value.chapters.filter((_, i) => i !== idx));

  const updateChapter = (idx: number, patch: Partial<{ time: number; title: string }>) =>
    set("chapters", value.chapters.map((c, i) => (i === idx ? { ...c, ...patch } : c)));

  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        boxShadow: (t) => t.shadows[1],
      }}
    >
      <Box component="header" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography component="h3" sx={{ fontSize: 14, fontWeight: 600, letterSpacing: "-0.01em" }}>
          Video
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
          Carries to the customer support portal's YouTube-like viewer.
        </Typography>
      </Box>

      <Box role="radiogroup" aria-label="Video source" sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {(["upload", "youtube", "vimeo", "embed"] as VideoKind[]).map((k) => {
          const selected = value.kind === k;
          return (
            <Box
              key={k}
              component="button"
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => set("kind", k)}
              sx={[
                {
                  borderRadius: 1,
                  border: 1,
                  px: 1.5,
                  py: 0.75,
                  fontSize: 12,
                  cursor: "pointer",
                  fontFamily: "inherit",
                },
                selected
                  ? {
                      borderColor: "primary.main",
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                      color: "primary.main",
                      fontWeight: 500,
                    }
                  : {
                      borderColor: "divider",
                      bgcolor: "transparent",
                      color: "text.disabled",
                      "&:hover": { borderColor: "text.disabled" },
                    },
              ]}
            >
              {labelForKind(k)}
            </Box>
          );
        })}
      </Box>

      {value.kind === "upload" ? (
        <Typography
          sx={{
            borderRadius: 1,
            bgcolor: "background.default",
            p: 1.5,
            fontSize: "12px",
            lineHeight: 1.6,
            color: "text.disabled",
          }}
        >
          Save the doc first, then attach the MP4 from the right-hand
          "Attached file" panel. The file IS the video — leave the
          Markdown body empty.
        </Typography>
      ) : (
        <Box sx={{ display: "grid", gap: 0.75 }}>
          <TextField
            id="video-external-url"
            label={
              value.kind === "youtube"
                ? "YouTube URL (any watch / share / shorts / embed form)"
                : value.kind === "vimeo"
                  ? "Vimeo URL"
                  : "Video link (Google Drive · Dropbox · direct MP4 · embed URL)"
            }
            type="url"
            inputMode="url"
            placeholder={
              value.kind === "youtube"
                ? "https://www.youtube.com/watch?v=…"
                : value.kind === "vimeo"
                  ? "https://vimeo.com/…"
                  : "https://drive.google.com/file/d/… or https://www.dropbox.com/scl/fi/…"
            }
            value={value.external_url}
            onChange={(e) => set("external_url", e.target.value)}
            fullWidth
          />
          {value.kind === "embed" && (
            <Typography sx={{ fontSize: 12, color: "text.disabled", lineHeight: 1.5 }}>
              Google Drive and Dropbox share links are converted to a playable
              source automatically — the file must be shared "anyone with the
              link". Other URLs are embedded as-is.
            </Typography>
          )}
        </Box>
      )}

      <Box sx={{ display: "grid", gap: 1.5, gridTemplateColumns: { sm: "repeat(3, 1fr)" } }}>
        <TextField
          id="video-duration"
          label="Duration (seconds)"
          type="number"
          inputProps={{ min: 0, step: 1 }}
          placeholder="e.g. 312"
          value={value.duration_seconds === "" ? "" : String(value.duration_seconds)}
          onChange={(e) =>
            set(
              "duration_seconds",
              e.target.value.trim() === "" ? "" : Math.max(0, Math.floor(Number(e.target.value))),
            )
          }
          fullWidth
        />
        <TextField
          id="video-thumb"
          label="Thumbnail URL (poster)"
          type="url"
          placeholder="https://…/poster.jpg"
          value={value.thumbnail_url}
          onChange={(e) => set("thumbnail_url", e.target.value)}
          fullWidth
        />
        <TextField
          id="video-captions"
          label="Captions URL (WebVTT)"
          type="url"
          placeholder="https://…/captions.vtt"
          value={value.captions_url}
          onChange={(e) => set("captions_url", e.target.value)}
          fullWidth
        />
      </Box>

      <ChapterEditor
        chapters={value.chapters}
        onAdd={addChapter}
        onRemove={removeChapter}
        onUpdate={updateChapter}
      />
    </Box>
  );
}

function ChapterEditor({
  chapters, onAdd, onRemove, onUpdate,
}: {
  chapters: Array<{ time: number; title: string }>;
  onAdd: () => void;
  onRemove: (idx: number) => void;
  onUpdate: (idx: number, patch: Partial<{ time: number; title: string }>) => void;
}) {
  return (
    <Box component="section" sx={{ display: "grid", gap: 1 }}>
      <Box component="header" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: "text.secondary" }}>
          Chapters
        </Typography>
        <Button type="button" variant="text" color="inherit" size="small" onClick={onAdd}>
          + Add chapter
        </Button>
      </Box>
      {chapters.length === 0 ? (
        <Typography
          sx={{
            borderRadius: 1,
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            px: 1.5,
            py: 1.5,
            fontSize: "12px",
            color: "text.disabled",
          }}
        >
          No chapters yet. Chapters give the customer a clickable rail
          beside the player.
        </Typography>
      ) : (
        <Box component="ol" sx={{ display: "grid", gap: 1, listStyle: "none", m: 0, p: 0 }}>
          {chapters.map((c, idx) => (
            <Box
              component="li"
              key={idx}
              sx={{ display: "grid", gridTemplateColumns: "6rem minmax(0,1fr) auto", alignItems: "center", gap: 1 }}
            >
              <TextField
                inputProps={{ "aria-label": `Chapter ${idx + 1} time` }}
                value={formatTimecode(c.time)}
                onChange={(e) => onUpdate(idx, { time: parseTimecode(e.target.value) })}
                onBlur={(e) => onUpdate(idx, { time: parseTimecode(e.target.value) })}
                sx={{ "& .MuiInputBase-input": { fontFamily: "ui-monospace, monospace", fontSize: 12 } }}
              />
              <TextField
                inputProps={{ "aria-label": `Chapter ${idx + 1} title` }}
                placeholder="Chapter title"
                value={c.title}
                onChange={(e) => onUpdate(idx, { title: e.target.value })}
              />
              <IconButton
                type="button"
                size="small"
                onClick={() => onRemove(idx)}
                aria-label={`Remove chapter ${idx + 1}`}
                sx={{ color: "text.disabled" }}
              >
                ×
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function labelForKind(k: VideoKind): string {
  if (k === "upload") return "Upload file";
  if (k === "youtube") return "YouTube";
  if (k === "vimeo") return "Vimeo";
  return "Embed URL";
}
